import { SHEETS_STRINGS } from './sheets.strings';

/**
 * AM-37: every export recalculates first.
 *
 * A workbook's cached cell values can be older than its formulas — the engine recalculates in the
 * background, so a download taken in the first moment after opening can carry the PREVIOUS numbers.
 * For `.xlsx` and `.ods` that self-corrects, because the formula travels with the file and Excel
 * recalculates on open. For `.xls`, CSV/TSV and PDF it does not: those formats carry values only,
 * so the wrong number is permanent and invisible.
 *
 * AM-37 steps 2–4: request a full recalculation, wait for it, and read values only afterwards. On
 * timeout the export fails with `CALC_TIMEOUT` and produces NO file — a missing download is
 * recoverable, a wrong one is not.
 *
 * AM-41 applies this to every format.
 */

/** AM-37 step 3. */
export const CALC_TIMEOUT_MS = 20_000;

/** The message AM-37 specifies, shown to the user as-is. */
export const CALC_TIMEOUT_MESSAGE = SHEETS_STRINGS['Recalculation took longer than 20 seconds.'];

/** Thrown when the engine does not settle in time. Distinguished so the editor can show the text. */
export class CalcTimeoutError extends Error {
  constructor() {
    super(CALC_TIMEOUT_MESSAGE);
    this.name = 'CALC_TIMEOUT';
  }
}

/** The slice of the engine's formula facade this needs, so a test does not need the engine. */
export interface FormulaFacade {
  executeCalculation(): void;
  whenComputingCompleteAsync(timeout?: number): Promise<boolean>;
}

export interface EngineApiLike {
  getFormula(): FormulaFacade;
}

/**
 * Recalculates and waits. Returns once values are safe to read; throws {@link CalcTimeoutError}
 * otherwise, so no caller can accidentally continue and write a file of stale numbers.
 *
 * `whenComputingCompleteAsync` is deprecated at 0.25.2 in favour of `onCalculationResultApplied`,
 * but AM-37 names it and an amendment beats the library's preference. If it is ever removed, the
 * replacement must keep the same contract: resolve only when results are applied, reject on timeout.
 */
export async function recalculateBeforeExport(
  engine: EngineApiLike | undefined,
  timeoutMs: number = CALC_TIMEOUT_MS,
): Promise<void> {
  const formula = engine?.getFormula();
  if (!formula) {
    // No engine means no formulas to be stale. Exporting a workbook of literals is correct.
    return;
  }

  formula.executeCalculation();
  const settled = await formula.whenComputingCompleteAsync(timeoutMs);
  if (!settled) {
    throw new CalcTimeoutError();
  }
}
